import { AbstractControl, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import {
  hasLowercase,
  hasUppercase,
  hasNumber,
  hasSpecialChar,
  hasMinLength
} from './password-helper';
export const emailValidator = [
  Validators.required,
  Validators.pattern('^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}$')
];
export const passwordPatternValidator = [
  Validators.required,
  Validators.pattern('^(?=.*[A-Z])(?=.*[a-z])(?=.*\\d)(?=.*[@$!%*?&])[A-Za-z\\d@$!%*?&]{8,}$')
];
export function checkPasswordMismatch(
  form: FormGroup,
  passwordField: string = 'password',
  confirmPasswordField: string = 'confirmPassword'
): boolean {
  const password = form.get(passwordField)?.value;
  const confirmPassword = form.get(confirmPasswordField)?.value;
  return !!(password && confirmPassword && password !== confirmPassword);
}
export function passwordValidator(control: AbstractControl): ValidationErrors | null {
  const value = control.value || '';
  if (!value) return null;
  const errors: any = {};
  if (!hasLowercase(value)) errors.noLowercase = true;
  if (!hasUppercase(value)) errors.noUppercase = true;
  if (!hasNumber(value)) errors.noNumber = true;
  if (!hasSpecialChar(value)) errors.noSpecialChar = true;
  if (!hasMinLength(value)) errors.tooShort = true;

  return Object.keys(errors).length ? errors : null;
}
export const minAgeValidator = (minAge: number): ValidatorFn => {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.value) return null;
    const age = parseInt(control.value, 10);
    if (isNaN(age)) return null;
    return age < minAge ? { minAge: { min: minAge, actual: age } } : null;
  };
};

export const maxAgeValidator = (maxAge: number): ValidatorFn => {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.value) return null;
    const age = parseInt(control.value, 10);
    if (isNaN(age)) return null;
    return age > maxAge ? { maxAge: { max: maxAge, actual: age } } : null;
  };
};
export const validateDestinations: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  const group = control as FormGroup;
  const from = group.get('from_destination')?.value;
  const to = group.get('to_destination')?.value;

  if (!from || !to) return null;

  const fromName = typeof from === 'object' ? from.name : from;
  const toName = typeof to === 'object' ? to.name : to;

  return fromName === toName ? { sameDestinations: true } : null;
};
export function markFormGroupTouched(formGroup: FormGroup): void {
  Object.keys(formGroup.controls).forEach((key) => {
    const control = formGroup.get(key);
    if (control instanceof FormControl) {
      control.markAsTouched();
      control.markAsDirty();
    } else if (control instanceof FormGroup) {
      markFormGroupTouched(control);
    }
  });
}
export function normalizeDialCode(code: string): string {
  if (!code) return '+91';
  const trimmed = code.toString().trim();
  const digits = trimmed.replace(/[^0-9]/g, '');
  return digits ? `+${digits}` : '+91';
}
export function normalizePhone(phone: string): string {
  if (!phone) return '';
  const trimmed = phone.toString().trim();
  const digits = trimmed.replace(/[^0-9]/g, '');
  if (trimmed.startsWith('+')) {
    return digits ? `+${digits}` : '';
  }
  return digits;
}
export function extractPhoneAndCode(
  phone: string, 
  countryCodes: Array<{ code: string; dialCode: string }>
): { countryCode: string; phoneNumber: string } {
  if (!phone) return { countryCode: '', phoneNumber: '' };
  
  const trimmed = phone.toString().trim();
  const digits = trimmed.replace(/[^0-9]/g, '');
  if (!digits) return { countryCode: '', phoneNumber: '' };
  const phoneWithPlus = trimmed.startsWith('+') ? trimmed : `+${digits}`;
  let countryCode = '';
  let phoneNumber = digits;
  const sortedCountries = [...countryCodes].sort((a, b) =>
    normalizeDialCode(b.dialCode).length - normalizeDialCode(a.dialCode).length
  );
  for (const country of sortedCountries) {
    const code = normalizeDialCode(country.dialCode);
    if (phoneWithPlus.startsWith(code)) {
      countryCode = code;
      phoneNumber = phoneWithPlus.substring(code.length).replace(/[^0-9]/g, '');
      break;
    }
  }
  
  return { countryCode, phoneNumber };
}
export function formatDate(date: any): string | null {
  if (!date) return null;
  const d = typeof date === 'string' ? new Date(date) : date;
  if (d instanceof Date && !isNaN(d.getTime())) {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
  return null;
}
export function convertToLocalDate(utcDate: string | Date | null | undefined): string | null {
  if (!utcDate) return null;
  const date = new Date(utcDate);
  if (isNaN(date.getTime())) return null;
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}
export function sanitizeFlightName(value: string): string {
  if (!value) return '';
  let v = value;
  v = v.replace(/[^A-Za-z\s]/g, '');
  v = v.replace(/\s{2,}/g, ' ');
  if (v.length > 50) v = v.slice(0, 50);
  return v;
}
